import { useEffect, useMemo, useState } from "react";
import type { GeoJsonObject } from "geojson";
import type { OrganizationMap } from "../../auth/data";
import { getOrganizationMapConfig, type OrganizationMapConfig } from "../commons/Constant";
import { getOrganizationBoundaryGeoJson } from "../api/organizationMap.api";
import { toOrganizationMapConfig } from "../utils/organizationMapConfig";

interface BoundaryState {
  url: string;
  data?: GeoJsonObject;
}

export function useOrganizationMapConfig(
  organizationId: string | undefined,
  organizationMap: OrganizationMap | null | undefined,
): OrganizationMapConfig {
  const [boundaryState, setBoundaryState] = useState<BoundaryState | null>(null);
  const boundaryUrl = organizationMap?.boundary_geojson_url;

  useEffect(() => {
    if (!boundaryUrl) {
      setBoundaryState(null);
      return;
    }

    const controller = new AbortController();
    setBoundaryState({ url: boundaryUrl });

    void getOrganizationBoundaryGeoJson(boundaryUrl, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setBoundaryState({ url: boundaryUrl, data });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setBoundaryState({ url: boundaryUrl });
        }
      });

    return () => controller.abort();
  }, [boundaryUrl]);

  return useMemo(() => {
    const runtimeConfig = toOrganizationMapConfig(
      organizationMap,
      boundaryState?.url === boundaryUrl ? boundaryState?.data : undefined,
    );

    return runtimeConfig ?? getOrganizationMapConfig(organizationId);
  }, [boundaryState, boundaryUrl, organizationId, organizationMap]);
}
